import React, { useEffect, useRef, useState } from 'react';
import { Stack, Typography } from '@mui/material';
import { Button } from 'reactstrap';
import { loadStripe, PaymentMethod } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import ContentCard from 'src/components/content/content-card';
import PaymentPreview from 'src/pages/signup/steps/payment/payment-preview';
import PaymentPopup from 'src/pages/account/sections/subscription/update-payment/update-payment-popup';
import User from 'src/models/User';
import colors from 'src/styles/equalution/colors';
import paymentAPI from 'src/pages/deprecated/payment/api';
import { useAppDispatch } from 'src/store/hooks';
import {
  setPaymentIntentLoading,
  setPaymentProcessing,
} from 'src/store/payment';
import { extractErrorMessage } from 'src/lib/utils';
import {
  getAllowedCampaignCode,
  getCampaignFromSession,
} from 'src/util/campaign.util';

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ''
);

interface ReactivatePaymentDetailsProps {
  user?: User | null;
  paymentMethod: PaymentMethod | null | undefined;
  setPaymentMethod?: (paymentMethod: PaymentMethod) => void;
  onReactivate: () => Promise<void>;
  isSubmitting?: boolean;
  priceId: string;
  totalPrice: string;
  setWalletPaymentIntentId: (id: string) => void;
  setNoIntentAvailable: (val: boolean) => void;
  setErrorMessage?: (msg: string) => void;
  ctaLabel?: string;
  campaignCode?: string | null;
}

export const ReactivatePaymentDetails: React.FC<
  ReactivatePaymentDetailsProps
> = ({
  user,
  paymentMethod,
  setPaymentMethod,
  onReactivate,
  isSubmitting = false,
  priceId,
  totalPrice,
  setWalletPaymentIntentId,
  setNoIntentAvailable,
  setErrorMessage,
  ctaLabel,
  campaignCode,
}) => {
  const dispatch = useAppDispatch();
  const [popupVisible, setPopupVisible] = useState<boolean>(false);
  const [stripeCustomer, setStripeCustomer] = useState<{ id: string } | null>(
    null
  );
  const [isIntentLoading, setIsIntentLoading] = useState(false);
  const [isIntentError, setIsIntentError] = useState(false);
  const desiredIntentKeyRef = useRef<string | null>(null);
  const requestedIntentKeyRef = useRef<string | null>(null);
  const intentDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleOpenPaymentPopup = () => {
    setPopupVisible(true);
  };

  // Create (or fetch) the Stripe customer up front, mirroring CardComponent —
  // the payment-intent call below needs a customerId to attach to.
  useEffect(() => {
    if (!user || stripeCustomer) return;
    const createCustomer = async () => {
      try {
        const { data } = await paymentAPI.post('stripe/customer/create', {
          email: user.email,
          userID: user.id,
        });
        setStripeCustomer(data.customer);
      } catch (error) {
        console.error('Failed to stripe create customer', error);
      }
    };
    createCustomer();
  }, [user, stripeCustomer]);

  // Create a PaymentIntent for the current plan before reactivation is
  // submitted, mirroring WalletComponent's createPaymentIntent. The wallets
  // purchase endpoint expects a subscription/invoice to already exist for
  // the customer — this call creates it as a side effect and hands back the
  // payment_intent id that startSubscription needs to forward.
  useEffect(() => {
    if (!stripeCustomer || !priceId || priceId.trim() === '') return;

    const intentKey = `${stripeCustomer.id}-${priceId}`;
    const intentKeyChanged = desiredIntentKeyRef.current !== intentKey;

    if (intentKeyChanged) {
      desiredIntentKeyRef.current = intentKey;
      requestedIntentKeyRef.current = null;
      setWalletPaymentIntentId('');
    } else if (requestedIntentKeyRef.current === intentKey) {
      return;
    }

    dispatch(setPaymentIntentLoading(true));
    setIsIntentLoading(true);
    setIsIntentError(false);
    setErrorMessage?.('');

    if (intentDebounceRef.current) {
      clearTimeout(intentDebounceRef.current);
    }
    intentDebounceRef.current = setTimeout(async () => {
      requestedIntentKeyRef.current = intentKey;
      try {
        const activeCampaignCode = getAllowedCampaignCode(
          campaignCode || getCampaignFromSession()
        );
        const paymentIntentPayload: Record<string, any> = {
          priceId,
          customerId: stripeCustomer.id,
          isZeroPayment: Number(totalPrice) === 0,
          isFreeTrial: false,
          isChallengePackage: false,
          addonPriceIds: [],
          claimGift: false,
        };
        if (activeCampaignCode) {
          paymentIntentPayload.campaignCode = activeCampaignCode;
        }

        const { data } = await paymentAPI.post(
          'stripe/create/payment-intent',
          paymentIntentPayload
        );

        if (desiredIntentKeyRef.current !== intentKey) return;

        setNoIntentAvailable(Boolean(data?.data?.noIntent));
        setWalletPaymentIntentId(
          data?.data?.subscription?.latest_invoice?.payment_intent?.id || ''
        );
      } catch (error) {
        console.error('Failed to create wallet payment intent', error);
        const status = (error as any)?.response?.status;
        const is4xx =
          (status >= 400 && status < 500) ||
          (error as any)?.response?.data?.code === 422;
        if (is4xx) {
          dispatch(setPaymentProcessing(false));
        }
        if (desiredIntentKeyRef.current === intentKey) {
          setIsIntentError(true);
          const apiMessage = extractErrorMessage(
            error,
            'Unable to prepare your subscription for reactivation. Please try again.'
          );
          setErrorMessage?.(apiMessage);
        }
      } finally {
        if (desiredIntentKeyRef.current === intentKey) {
          dispatch(setPaymentIntentLoading(false));
          setIsIntentLoading(false);
        }
      }
    }, 800);

    return () => {
      if (intentDebounceRef.current) {
        clearTimeout(intentDebounceRef.current);
      }
    };
  }, [
    stripeCustomer,
    priceId,
    totalPrice,
    dispatch,
    setNoIntentAvailable,
    setWalletPaymentIntentId,
    setErrorMessage,
    campaignCode,
  ]);

  const handleSubmit = () => {
    if (isSubmitting || isIntentLoading || isIntentError) return;
    onReactivate();
  };

  return (
    <>
      <Stack
        width="100%"
        maxWidth="400px"
        gap="16px"
        margin="0 auto"
        alignItems="center">
        <Typography
          sx={{
            fontSize: '20px',
            fontWeight: 700,
            color: colors.OFF_BLACK,
            textAlign: 'center',
            mt: 1,
          }}>
          Payment Details
        </Typography>

        <ContentCard lighter>
          <PaymentPreview paymentMethod={paymentMethod || undefined} />
          <Button
            size="sm"
            outline
            onClick={handleOpenPaymentPopup}
            disabled={!user}>
            Change
          </Button>
        </ContentCard>

        <Button
          block
          disabled={isSubmitting || isIntentLoading || isIntentError}
          onClick={handleSubmit}>
          {isSubmitting
            ? 'Processing...'
            : isIntentLoading
            ? 'Preparing...'
            : ctaLabel ?? 'Reactivate subscription'}
        </Button>
      </Stack>

      {popupVisible && !!user && setPaymentMethod && (
        <Elements stripe={stripePromise}>
          <PaymentPopup
            user={user}
            setPaymentMethod={setPaymentMethod}
            setPopupVisible={setPopupVisible}
          />
        </Elements>
      )}
    </>
  );
};
